import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Check,
  X,
  Copy,
  ExternalLink,
  Phone,
  Key,
  Shield,
  Loader2,
  RefreshCw,
  TestTube,
  Send,
  Image,
  Upload,
  HardDrive,
  Database,
  Clock,
  Settings2,
  Play,
  Save,
  AlertTriangle,
  FileText,
  FolderOpen,
  Trash,
  Calendar,
  Cpu,
  Activity,
  Server,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ SYSTEM SETTINGS COMPONENT ============

// System Configuration Panel - Real-time server stats
function SystemConfigPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/system-stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching system stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (percent) => {
    if (percent < 50) return "text-green-600 bg-green-50 border-green-200";
    if (percent < 80) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const getProgressColor = (percent) => {
    if (percent < 50) return "bg-green-500";
    if (percent < 80) return "bg-amber-500";
    return "bg-red-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-4">
        <Card className={getStatusColor(stats?.cpu?.percent || 0)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Cpu className="h-5 w-5" />
                <span className="font-medium">CPU</span>
              </div>
              <span className="text-lg font-bold">{stats?.cpu?.percent || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className={`h-2 rounded-full ${getProgressColor(stats?.cpu?.percent || 0)}`} style={{width: `${stats?.cpu?.percent || 0}%`}}></div>
            </div>
            <p className="text-xs mt-1">{stats?.cpu?.cores || 0} cores</p>
          </CardContent>
        </Card>

        <Card className={getStatusColor(stats?.ram?.percent || 0)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />
                <span className="font-medium">RAM</span>
              </div>
              <span className="text-lg font-bold">{stats?.ram?.percent || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className={`h-2 rounded-full ${getProgressColor(stats?.ram?.percent || 0)}`} style={{width: `${stats?.ram?.percent || 0}%`}}></div>
            </div>
            <p className="text-xs mt-1">{stats?.ram?.used_gb?.toFixed(1) || 0} / {stats?.ram?.total_gb?.toFixed(1) || 0} GB</p>
          </CardContent>
        </Card>

        <Card className={getStatusColor(stats?.disk?.percent || 0)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                <span className="font-medium">Main Disk</span>
              </div>
              <span className="text-lg font-bold">{stats?.disk?.percent || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className={`h-2 rounded-full ${getProgressColor(stats?.disk?.percent || 0)}`} style={{width: `${stats?.disk?.percent || 0}%`}}></div>
            </div>
            <p className="text-xs mt-1">{stats?.disk?.used_gb?.toFixed(1) || 0} / {stats?.disk?.total_gb?.toFixed(1) || 0} GB</p>
          </CardContent>
        </Card>

        {stats?.images_disk?.mounted && (
        <Card className={getStatusColor(stats?.images_disk?.percent || 0)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                <span className="font-medium">Images</span>
              </div>
              <span className="text-lg font-bold">{stats?.images_disk?.percent || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className={`h-2 rounded-full ${getProgressColor(stats?.images_disk?.percent || 0)}`} style={{width: `${stats?.images_disk?.percent || 0}%`}}></div>
            </div>
            <p className="text-xs mt-1">{stats?.images_disk?.used_gb?.toFixed(1) || 0} / {stats?.images_disk?.total_gb?.toFixed(1) || 0} GB</p>
          </CardContent>
        </Card>
        )}

        {stats?.designs_disk?.mounted && (
        <Card className={getStatusColor(stats?.designs_disk?.percent || 0)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                <span className="font-medium">Designs</span>
              </div>
              <span className="text-lg font-bold">{stats?.designs_disk?.percent || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className={`h-2 rounded-full ${getProgressColor(stats?.designs_disk?.percent || 0)}`} style={{width: `${stats?.designs_disk?.percent || 0}%`}}></div>
            </div>
            <p className="text-xs mt-1">{stats?.designs_disk?.used_gb?.toFixed(1) || 0} / {stats?.designs_disk?.total_gb?.toFixed(1) || 0} GB</p>
          </CardContent>
        </Card>
        )}

        <Card className="text-blue-600 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5" />
              <span className="font-medium">Uptime</span>
            </div>
            <p className="text-lg font-bold">{stats?.uptime?.formatted || "Unknown"}</p>
            <p className="text-xs mt-1">Server running</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SystemSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/system-settings`),
        axios.get(`${API}/admin/storage-stats`)
      ]);
      setSettings(settingsRes.data.settings);
      setStorageStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching system settings:", error);
      toast.error("Failed to load system settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/system-settings`, settings);
      toast.success("Settings saved successfully");
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleRunCleanup = async () => {
    try {
      setCleaning(true);
      const response = await axios.post(`${API}/admin/cleanup`);
      const { results } = response.data;
      
      const totalDeleted = 
        (results.images?.deleted_files || 0) + 
        (results.pdfs?.deleted_pdfs || 0) + 
        (results.trash?.deleted_trash || 0);
      
      const totalFreed = 
        (results.images?.freed_mb || 0) + 
        (results.pdfs?.freed_mb || 0) + 
        (results.trash?.freed_mb || 0);
      
      if (totalDeleted > 0) {
        toast.success(`Cleanup complete! Deleted ${totalDeleted} files, freed ${totalFreed.toFixed(2)} MB`);
      } else {
        toast.info("No files to clean up");
      }
      
      // Refresh storage stats
      const statsRes = await axios.get(`${API}/admin/storage-stats`);
      setStorageStats(statsRes.data);
    } catch (error) {
      console.error("Error running cleanup:", error);
      toast.error("Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const updateSetting = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Storage Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Image className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Images</p>
                <p className="text-lg font-bold text-blue-800">{storageStats?.images?.size || "0 B"}</p>
                <p className="text-xs text-blue-500">{storageStats?.images?.file_count || 0} files</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium">PDFs</p>
                <p className="text-lg font-bold text-purple-800">{storageStats?.pdfs?.size || "0 B"}</p>
                <p className="text-xs text-purple-500">{storageStats?.pdfs?.file_count || 0} files</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Trash className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-orange-600 font-medium">Trash</p>
                <p className="text-lg font-bold text-orange-800">{storageStats?.trash?.size || "0 B"}</p>
                <p className="text-xs text-orange-500">{storageStats?.trash?.file_count || 0} files</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <HardDrive className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Total</p>
                <p className="text-lg font-bold text-green-800">{storageStats?.total?.size || "0 B"}</p>
                <p className="text-xs text-green-500">All storage</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Auto-Cleanup Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Auto-Cleanup Settings
              </CardTitle>
              <CardDescription>
                Configure automatic deletion of old files to save storage space
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="cleanup-enabled" className="text-sm">Enable Auto-Cleanup</Label>
              <Switch
                id="cleanup-enabled"
                checked={settings?.auto_cleanup?.enabled}
                onCheckedChange={(checked) => updateSetting("auto_cleanup", "enabled", checked)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Image className="h-4 w-4 text-blue-500" />
                Keep Images (Days)
              </Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings?.auto_cleanup?.images_retention_days || 4}
                onChange={(e) => updateSetting("auto_cleanup", "images_retention_days", parseInt(e.target.value))}
                disabled={!settings?.auto_cleanup?.enabled}
              />
              <p className="text-xs text-muted-foreground">Customer photos older than this will be deleted</p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-purple-500" />
                Keep PDFs (Days)
              </Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings?.auto_cleanup?.pdf_retention_days || 2}
                onChange={(e) => updateSetting("auto_cleanup", "pdf_retention_days", parseInt(e.target.value))}
                disabled={!settings?.auto_cleanup?.enabled}
              />
              <p className="text-xs text-muted-foreground">Generated PDFs older than this will be deleted</p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Trash className="h-4 w-4 text-orange-500" />
                Keep Trash (Days)
              </Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings?.auto_cleanup?.trash_retention_days || 7}
                onChange={(e) => updateSetting("auto_cleanup", "trash_retention_days", parseInt(e.target.value))}
                disabled={!settings?.auto_cleanup?.enabled}
              />
              <p className="text-xs text-muted-foreground">Trash items older than this will be permanently deleted</p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-green-500" />
                Cleanup Interval (Hours)
              </Label>
              <Select
                value={String(settings?.auto_cleanup?.cleanup_interval_hours || 6)}
                onValueChange={(value) => updateSetting("auto_cleanup", "cleanup_interval_hours", parseInt(value))}
                disabled={!settings?.auto_cleanup?.enabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Every 1 hour</SelectItem>
                  <SelectItem value="3">Every 3 hours</SelectItem>
                  <SelectItem value="6">Every 6 hours</SelectItem>
                  <SelectItem value="12">Every 12 hours</SelectItem>
                  <SelectItem value="24">Every 24 hours</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">How often to run automatic cleanup</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium">Manual Cleanup</p>
              <p className="text-xs text-muted-foreground">Run cleanup now based on current settings</p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={cleaning}>
                  {cleaning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Run Cleanup Now
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Run Cleanup?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete:
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Images older than {settings?.auto_cleanup?.images_retention_days} days</li>
                      <li>PDFs older than {settings?.auto_cleanup?.pdf_retention_days} days</li>
                      <li>Trash items older than {settings?.auto_cleanup?.trash_retention_days} days</li>
                    </ul>
                    <p className="mt-2 text-amber-600 font-medium">This action cannot be undone!</p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRunCleanup}>
                    Yes, Run Cleanup
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Database Cleanup Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                Database Cleanup Settings
              </CardTitle>
              <CardDescription>
                Configure automatic cleanup for orders and conversations (with backup)
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="db-cleanup-enabled" className="text-sm">Enable Database Cleanup</Label>
              <Switch
                id="db-cleanup-enabled"
                checked={settings?.database_cleanup?.enabled}
                onCheckedChange={(checked) => updateSetting("database_cleanup", "enabled", checked)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-blue-500" />
                Keep Orders (Days)
              </Label>
              <Input
                type="number"
                min="7"
                max="365"
                value={settings?.database_cleanup?.orders_retention_days || 30}
                onChange={(e) => updateSetting("database_cleanup", "orders_retention_days", parseInt(e.target.value))}
                disabled={!settings?.database_cleanup?.enabled}
              />
              <p className="text-xs text-muted-foreground">Customer orders older than this will be deleted</p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                Keep Conversations (Days)
              </Label>
              <Input
                type="number"
                min="7"
                max="365"
                value={settings?.database_cleanup?.conversations_retention_days || 30}
                onChange={(e) => updateSetting("database_cleanup", "conversations_retention_days", parseInt(e.target.value))}
                disabled={!settings?.database_cleanup?.enabled}
              />
              <p className="text-xs text-muted-foreground">Chat conversations older than this will be deleted</p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Save className="h-4 w-4 text-green-500" />
                Keep Backups
              </Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings?.database_cleanup?.keep_backups || 10}
                onChange={(e) => updateSetting("database_cleanup", "keep_backups", parseInt(e.target.value))}
                disabled={!settings?.database_cleanup?.enabled}
              />
              <p className="text-xs text-muted-foreground">Number of backup files to keep</p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-amber-500" />
                Backup Before Delete
              </Label>
              <div className="flex items-center gap-2 pt-2">
                <Switch
                  id="backup-before-delete"
                  checked={settings?.database_cleanup?.backup_before_delete}
                  onCheckedChange={(checked) => updateSetting("database_cleanup", "backup_before_delete", checked)}
                  disabled={!settings?.database_cleanup?.enabled}
                />
                <Label htmlFor="backup-before-delete" className="text-sm">
                  {settings?.database_cleanup?.backup_before_delete ? "Enabled" : "Disabled"}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">Auto-backup data before deleting</p>
            </div>
          </div>
          
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">Your Data is Safe</p>
                <p className="text-xs text-green-600 mt-1">
                  When "Backup Before Delete" is enabled, old records are automatically saved to a JSON file 
                  before deletion. You can download these backups anytime from the backup history below.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backup Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                Manual Backup
              </CardTitle>
              <CardDescription>
                Download backups of your old data</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Full Backup Feature Coming Soon</p>
                <p className="text-xs text-amber-600 mt-1">
                  Auto-backups of old records (before deletion) are already working. Full database backup 
                  to external storage is coming soon.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button 
            onClick={handleSaveSettings} 
            disabled={saving}
            size="lg"
            className="shadow-lg"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      )}
    </div>
  );
}

// Test Message Simulator Component
function TestMessageSimulator() {
  const [testPhone, setTestPhone] = useState("+919876543210");
  const [testName, setTestName] = useState("Test Customer");
  const [testMessage, setTestMessage] = useState("Hi, I want to place an order. Order ID: 1234567890123");
  const [sending, setSending] = useState(false);

  const handleSimulate = async () => {
    if (!testPhone || !testMessage) {
      toast.error("Please fill in phone number and message");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/test/simulate-message`, {
        phone_number: testPhone,
        customer_name: testName,
        message: testMessage,
      });

      if (response.data.success) {
        toast.success("Test message sent! Check the Dashboard.");
        setTestMessage("");
      }
    } catch (error) {
      console.error("Error simulating message:", error);
      toast.error("Failed to simulate message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="test-phone">Phone Number</Label>
          <Input
            id="test-phone"
            placeholder="+919876543210"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="test-name">Customer Name</Label>
          <Input
            id="test-name"
            placeholder="Test Customer"
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="test-message">Message</Label>
        <Textarea
          id="test-message"
          placeholder="Hi, I want to place an order..."
          value={testMessage}
          onChange={(e) => setTestMessage(e.target.value)}
          rows={3}
        />
      </div>
      <Button onClick={handleSimulate} disabled={sending} className="w-full">
        {sending ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Send className="h-4 w-4 mr-2" />
        )}
        Simulate Incoming Message
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        This will create a test conversation and message in the Dashboard without needing Meta webhook.
      </p>
    </div>
  );
}

// Test Image Upload Simulator Component
function TestImageSimulator() {
  const [testPhone, setTestPhone] = useState("+919876543210");
  const [testName, setTestName] = useState("Test Customer");
  const [imageCount, setImageCount] = useState(3);
  const [uploading, setUploading] = useState(false);

  const handleSimulateImages = async () => {
    if (!testPhone) {
      toast.error("Please fill in phone number");
      return;
    }

    setUploading(true);
    try {
      const response = await axios.post(`${API}/test/simulate-image`, {
        phone_number: testPhone,
        customer_name: testName,
        image_count: imageCount,
      });

      if (response.data.success) {
        toast.success(`${imageCount} test image(s) created! Check the Orders page.`);
      }
    } catch (error) {
      console.error("Error simulating images:", error);
      toast.error("Failed to simulate images");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="img-phone">Phone Number</Label>
          <Input
            id="img-phone"
            placeholder="+919876543210"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="img-name">Customer Name</Label>
          <Input
            id="img-name"
            placeholder="Test Customer"
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="img-count">Number of Images</Label>
          <Input
            id="img-count"
            type="number"
            min={1}
            max={20}
            value={imageCount}
            onChange={(e) => setImageCount(parseInt(e.target.value) || 1)}
          />
        </div>
      </div>
      <Button onClick={handleSimulateImages} disabled={uploading} className="w-full" variant="secondary">
        {uploading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Upload className="h-4 w-4 mr-2" />
        )}
        Simulate Image Upload
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        This will create test images in the folder structure: YYYY-MM-DD/PHONE_NUMBER.SESSION_ID/
      </p>
    </div>
  );
}


// Quick Reply Templates Panel Component
function QuickReplyTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTemplate, setNewTemplate] = useState({ name: "", content: "", category: "general" });
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState({ name: "", content: "", category: "general" });
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const addTemplate = async () => {
    if (!newTemplate.name.trim() || !newTemplate.content.trim()) {
      toast.error("Please fill in both name and content");
      return;
    }
    try {
      await axios.post(`${API}/api/templates`, newTemplate);
      toast.success("Template added!");
      setNewTemplate({ name: "", content: "", category: "general" });
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to add template");
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/api/templates/${id}`);
      toast.success("Template deleted!");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const categories = ["general", "greeting", "support", "sales", "follow-up"];

  if (loading) {
    return <div className="flex justify-center p-4"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Add New Template */}
      <div className="p-4 border rounded-lg bg-slate-50">
        <h4 className="font-semibold mb-3">Add New Template</h4>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="template-name">Name / Shortcode</Label>
              <Input
                id="template-name"
                placeholder="e.g., greeting1"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="template-category">Category</Label>
              <select
                id="template-category"
                className="w-full h-10 px-3 border rounded-md"
                value={newTemplate.category}
                onChange={(e) => setNewTemplate({...newTemplate, category: e.target.value})}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label htmlFor="template-content">Message Content</Label>
            <textarea
              id="template-content"
              placeholder="Type your quick reply message here..."
              className="w-full min-h-[80px] px-3 py-2 border rounded-md"
              value={newTemplate.content}
              onChange={(e) => setNewTemplate({...newTemplate, content: e.target.value})}
            />
          </div>
          <Button onClick={addTemplate} className="w-full">
            <Plus className="h-4 w-4 mr-2" /> Add Template
          </Button>
        </div>
      </div>

      {/* Existing Templates */}
      <div className="space-y-2">
        <h4 className="font-semibold">Saved Templates ({templates.length})</h4>
        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 text-center border rounded-lg">
            No templates yet. Add your first quick reply above!
          </p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {templates.map((template) => (
              <div key={template.id} className="p-3 border rounded-lg flex items-start justify-between gap-3 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{template.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-slate-200 rounded-full">{template.category}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{template.content}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => deleteTemplate(template.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [formData, setFormData] = useState({
    phone_number_id: "",
    business_account_id: "",
    access_token: "",
    app_secret: "",
    phone_display_name: "",
  });

  useEffect(() => {
    fetchConfigs();
    fetchWebhookInfo();
  }, []);

  const fetchWebhookInfo = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/webhook-info`);
      setWebhookInfo(response.data);
    } catch (error) {
      console.error("Error fetching webhook info:", error);
    }
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/whatsapp/configs`);
      setConfigs(response.data);
    } catch (error) {
      console.error("Error fetching configs:", error);
      toast.error("Failed to load configurations");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.phone_number_id || !formData.access_token || !formData.app_secret) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/whatsapp/configs`, formData);
      toast.success("WhatsApp configuration added successfully");
      setDialogOpen(false);
      setFormData({
        phone_number_id: "",
        business_account_id: "",
        access_token: "",
        app_secret: "",
        phone_display_name: "",
      });
      fetchConfigs();
    } catch (error) {
      console.error("Error creating config:", error);
      toast.error("Failed to create configuration");
    }
  };

  const handleDelete = async (configId) => {
    try {
      await axios.delete(`${API}/whatsapp/configs/${configId}`);
      toast.success("Configuration deleted");
      fetchConfigs();
    } catch (error) {
      console.error("Error deleting config:", error);
      toast.error("Failed to delete configuration");
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await axios.put(`${API}/whatsapp/configs/${config.id}`, {
        is_active: !config.is_active,
      });
      toast.success(config.is_active ? "Configuration disabled" : "Configuration enabled");
      fetchConfigs();
    } catch (error) {
      console.error("Error updating config:", error);
      toast.error("Failed to update configuration");
    }
  };

  const handleTestConnection = async (configId) => {
    setTestingId(configId);
    try {
      const response = await axios.post(`${API}/whatsapp/configs/${configId}/test`);
      if (response.data.success) {
        toast.success(`Connected! Phone: ${response.data.phone_number}`);
      } else {
        toast.error(`Connection failed: ${response.data.error}`);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      toast.error("Connection test failed");
    } finally {
      setTestingId(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success("Copied to clipboard");
        } catch (err) {
          toast.error("Failed to copy to clipboard");
        }
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-30 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
            <p className="text-sm text-muted-foreground">
              Configure WhatsApp Business API connections
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Webhook Configuration Card */}
        <Card className="shadow-soft border-2 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Webhook Configuration
            </CardTitle>
            <CardDescription>
              Use these values in your Meta Developer Console for webhook setup
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Callback URL */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Callback URL</Label>
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <code className="flex-1 text-sm font-mono break-all">
                  {webhookInfo?.webhook_url || `${BACKEND_URL}/api/whatsapp/webhook`}
                </code>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(webhookInfo?.webhook_url || `${BACKEND_URL}/api/whatsapp/webhook`)}
                        data-testid="copy-webhook-url"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy URL</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Verify Token */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Verify Token</Label>
              <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <code className="flex-1 text-sm font-mono text-emerald-800">
                  {webhookInfo?.verify_token || "WCOAS_SUPER_SECRET_TOKEN"}
                </code>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(webhookInfo?.verify_token || "WCOAS_SUPER_SECRET_TOKEN")}
                        data-testid="copy-verify-token"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy Verify Token</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <Separator />

            {/* Step by step instructions */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Step-by-Step Setup:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Meta Developer Console</a></li>
                <li>Select your App → WhatsApp → Configuration</li>
                <li>In the Webhook section, click <strong>"Edit"</strong></li>
                <li>Paste the <strong>Callback URL</strong> from above</li>
                <li>Paste the <strong>Verify Token</strong> from above</li>
                <li>Click <strong>"Verify and save"</strong></li>
                <li>Subscribe to webhook fields: <Badge variant="outline" className="ml-1">messages</Badge> <Badge variant="outline" className="ml-1">message_status</Badge></li>
              </ol>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm">
              <ExternalLink className="h-4 w-4 text-primary" />
              <a
                href="https://developers.facebook.com/apps/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Open Meta Developer Console
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Phone Numbers Card */}
        <Card className="shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-primary" />
                Connected Phone Numbers
              </CardTitle>
              <CardDescription>
                Manage your WhatsApp Business API phone numbers
              </CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-config-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Phone Number
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>Add WhatsApp Configuration</DialogTitle>
                    <DialogDescription>
                      Enter your Meta WhatsApp Business API credentials
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone_display_name">Display Name</Label>
                      <Input
                        id="phone_display_name"
                        placeholder="e.g., Customer Support"
                        value={formData.phone_display_name}
                        onChange={(e) =>
                          setFormData({ ...formData, phone_display_name: e.target.value })
                        }
                        data-testid="input-display-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone_number_id">Phone Number ID *</Label>
                      <Input
                        id="phone_number_id"
                        placeholder="Enter Phone Number ID from Meta"
                        value={formData.phone_number_id}
                        onChange={(e) =>
                          setFormData({ ...formData, phone_number_id: e.target.value })
                        }
                        required
                        data-testid="input-phone-number-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="business_account_id">Business Account ID</Label>
                      <Input
                        id="business_account_id"
                        placeholder="Enter Business Account ID"
                        value={formData.business_account_id}
                        onChange={(e) =>
                          setFormData({ ...formData, business_account_id: e.target.value })
                        }
                        data-testid="input-business-account-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="access_token">Access Token *</Label>
                      <Input
                        id="access_token"
                        type="password"
                        placeholder="Enter your permanent access token"
                        value={formData.access_token}
                        onChange={(e) =>
                          setFormData({ ...formData, access_token: e.target.value })
                        }
                        required
                        data-testid="input-access-token"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="app_secret">App Secret *</Label>
                      <Input
                        id="app_secret"
                        type="password"
                        placeholder="Enter your app secret"
                        value={formData.app_secret}
                        onChange={(e) =>
                          setFormData({ ...formData, app_secret: e.target.value })
                        }
                        required
                        data-testid="input-app-secret"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-config-button">
                      Add Configuration
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : configs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Phone className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No phone numbers configured</p>
                <p className="text-sm">Add a WhatsApp Business API configuration to get started</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-3">
                  {configs.map((config) => (
                    <div
                      key={config.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      data-testid={`config-item-${config.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Phone className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {config.phone_display_name || "Unnamed Phone"}
                            {config.is_active ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                Active
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-50 text-gray-600">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground font-mono">
                            ID: {config.phone_number_id}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  {config.is_active ? "On" : "Off"}
                                </span>
                                <Switch
                                  checked={config.is_active}
                                  onCheckedChange={() => handleToggleActive(config)}
                                  data-testid={`toggle-config-${config.id}`}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {config.is_active ? "Disable" : "Enable"} this configuration
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        <Separator orientation="vertical" className="h-6" />
                        
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleTestConnection(config.id)}
                                disabled={testingId === config.id}
                                data-testid={`test-config-${config.id}`}
                              >
                                {testingId === config.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Test Connection</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(config.webhook_verify_token)}
                                data-testid={`copy-verify-token-${config.id}`}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Copy Verify Token</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-config-${config.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Configuration?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete this WhatsApp configuration. 
                                All associated conversations will remain but won't receive new messages.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(config.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Test Mode Card */}
        <Card className="shadow-soft border-2 border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TestTube className="h-5 w-5 text-amber-600" />
              Test Mode (Simulate Messages)
            </CardTitle>
            <CardDescription>
              Test the system without Meta webhook verification. Simulate incoming customer messages.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TestMessageSimulator />
          </CardContent>
        </Card>

        {/* Test Image Upload Card */}
        <Card className="shadow-soft border-2 border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-blue-600" />
              Test Mode (Simulate Image Upload)
            </CardTitle>
            <CardDescription>
              Simulate customer image uploads to test the image storage system without WhatsApp.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TestImageSimulator />
          </CardContent>
        </Card>


        {/* System Configuration Section */}
        <Card className="shadow-soft border-2 border-blue-200 bg-blue-50/30 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              System Configuration
            </CardTitle>
            <CardDescription>
              Real-time AWS server resource monitoring (auto-refreshes every 10s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SystemConfigPanel />
          </CardContent>
        </Card>

        {/* System Settings Section */}
        <Card className="shadow-soft border-2 border-green-200 bg-green-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-green-600" />
              System Settings
            </CardTitle>
            <CardDescription>
              Configure auto-cleanup, storage management, and backup options
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SystemSettingsPanel />
          </CardContent>
        </Card>

        {/* Quick Reply Templates Card */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              Quick Reply Templates
            </CardTitle>
            <CardDescription>
              Create shortcut messages for quick replies. Use these in chat by clicking the Templates button.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <QuickReplyTemplatesPanel />
          </CardContent>
        </Card>

        {/* Quick Setup Guide Card */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Quick Setup Guide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">1. Create Meta App</h4>
                <p className="text-sm text-muted-foreground">
                  Go to developers.facebook.com and create a new Business app with WhatsApp product.
                </p>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">2. Get Credentials</h4>
                <p className="text-sm text-muted-foreground">
                  From your Meta app dashboard, copy the Phone Number ID, Access Token, and App Secret.
                </p>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">3. Configure Webhook</h4>
                <p className="text-sm text-muted-foreground">
                  In Meta Console, set up webhook with the URL above and the verify token from your config.
                </p>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">4. Subscribe Events</h4>
                <p className="text-sm text-muted-foreground">
                  Subscribe to messages, message_status, and media events in the webhook configuration.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
